import classnames from "classnames";
import { forwardRef, LegacyRef } from "react";
import styles from "./Text.module.scss";
import { useLayoutPropsUtil } from "../../internal/hooks";
import { AiMark } from "../AiMark";
import {
  BodyTextInlineProps,
  BodyTextProps,
  EyebrowProps,
  HeadlineProps,
} from "./types";

/**
 * Props for the Text component
 * @extends HeadlineProps<"h1" | "h2" | "h3" | "h4" | "h5" | "h6">
 * @extends EyebrowProps
 * @extends BodyTextProps
 * @extends BodyTextInlineProps
 */
export type TextProps =
  | HeadlineProps<"h1">
  | HeadlineProps<"h2">
  | HeadlineProps<"h3">
  | HeadlineProps<"h4">
  | HeadlineProps<"h5">
  | HeadlineProps<"h6">
  | EyebrowProps
  | BodyTextProps
  | BodyTextInlineProps;

/**
 * Text component for displaying various types of text content with consistent styling.
 *
 * Features:
 * - Multiple text variants (headline, eyebrow, body)
 * - Semantic HTML elements (h1-h6, p, span)
 * - Multiple size options for each variant
 *
 * @example
 * <Text variant="headline" el="h1" size="large">Main Heading</Text>
 * <Text variant="headline" el="h2" size="medium">Sub Heading</Text>
 *
 * @example
 * <Text variant="eyebrow" size="small">Category Label</Text>
 *
 * @example
 * <Text size="large">Large body text</Text>
 * <Text subdued>Deemphasized text</Text>
 * <Text inline>Inline text within other text</Text>
 */
export const Text = forwardRef<
  HTMLHeadingElement | HTMLSpanElement | HTMLParagraphElement,
  TextProps
>((props, ref) => {
  const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
  if (props.variant === "headline") {
    const {
      variant: _,
      children,
      className,
      el: HeadlineElement,
      size = "medium",
      style,
      aiMark,
      ...rest
    } = componentProps;

    const TextClasses = classnames(styles["headline"], className, {
      [styles[`${size}`]]: size,
    });

    const styleCombined = { ...style, ...layoutStyles };

    const aiMarkSize = size === "small" ? "medium" : size;

    const aiMarkNode = aiMark && (
      <AiMark
        className={styles["headline-ai-mark"]}
        type="gradient"
        collapsePadding="block"
        size={aiMarkSize}
        popoverOrTooltipConfig={typeof aiMark === "object" ? aiMark : undefined}
      />
    );

    return (
      // @ts-expect-error ts(2604) HeadlineElement will work as element tag
      <HeadlineElement
        ref={ref as LegacyRef<HTMLHeadingElement>}
        className={TextClasses}
        data-anv="text"
        style={styleCombined}
        {...rest}
      >
        {aiMarkNode ? (
          <span className={styles["headline-with-ai-mark"]}>
            {children}
            {aiMarkNode}
          </span>
        ) : (
          children
        )}
      </HeadlineElement>
    );
  } else if (props.variant === "eyebrow") {
    const {
      variant: _,
      children,
      className,
      size = "medium",
      style,
      ...rest
    } = componentProps;

    const TextClasses = classnames(styles["eyebrow"], className, {
      [styles[`${size}`]]: size,
    });

    const styleCombined = { ...style, ...layoutStyles };

    return (
      <span
        ref={ref as LegacyRef<HTMLSpanElement>}
        className={TextClasses}
        data-anv="text"
        style={styleCombined}
        {...rest}
      >
        {children}
      </span>
    );
  } else {
    const {
      variant: _,
      children,
      className,
      subdued,
      inline,
      style,
      size = "medium",
      ...rest
    } = componentProps;

    const TextClasses = classnames(styles["bodytext"], className, {
      [styles[`${size}`]]: size,
      [styles["subdued"]]: subdued,
      [styles["inline"]]: inline,
    });

    const styleCombined = { ...style, ...layoutStyles };

    if (inline) {
      return (
        <span
          ref={ref as LegacyRef<HTMLSpanElement>}
          className={TextClasses}
          data-anv="text"
          style={styleCombined}
          {...rest}
        >
          {children}
        </span>
      );
    } else {
      return (
        <p
          ref={ref as LegacyRef<HTMLParagraphElement>}
          className={TextClasses}
          data-anv="text"
          style={styleCombined}
          {...rest}
        >
          {children}
        </p>
      );
    }
  }
});

Text.displayName = "Text";
